import { runOnWebWorker } from 'app/run-on-web-worker';
import { getStore } from 'app/hermes-redux';
import PageTitle from 'head/state/actions';
import { getRouterState } from './router-state-provider';
import {
  handleRestrictions,
  handleComplexRestrictions,
} from 'globals/restrictions-handler';
import type { Module, Restrictions, RestrictionAction } from 'router/types';
import { goTo } from 'router/navigation';
import {
  performConsentRedirectionFlow,
  backgroundRefreshConsentsPageBlacklist,
} from 'pages/consent/helpers';
import type { AppRole } from 'app/state/session/types';
import USER_ROLES from 'constants/user-roles.constant';
import { AppointmentsRoutes } from 'pages/appointments/state/constants';
import { AppLogger, LogTypes } from '@workivate/tho-web-shared';

export const updatePageTitle = (currentRoute: Module) => {
  if (currentRoute?.data?.title) {
    getStore().dispatch(
      PageTitle.setCurrentSection(polyglot.t(currentRoute.data.title)),
    );
  }
};

/*
  Check if user has the permission the move to a route
*/
export function isUserAuthorized(
  route: Module | undefined | null,
  authorizedRoles?: AppRole[] | null,
) {
  const userRole = getStore().getState().session.role;

  return (
    route &&
    (!authorizedRoles ||
      authorizedRoles.some(
        (r: AppRole) => r === userRole || r === USER_ROLES.all,
      ))
  );
}

export const onVisibilityChange = () => {
  const routerState = getRouterState();

  if (
    document.visibilityState === 'visible' &&
    !backgroundRefreshConsentsPageBlacklist.includes(
      routerState.currentRoute?.name,
    )
  ) {
    // Additional safeguard: Check if we should defer the post-login flow
    // This prevents consents from showing when user returns to tab during signup
    const state = getStore().getState();
    const shouldDeferPostLoginFlow = state.consent.shouldResumePostLoginFlow;

    if (!shouldDeferPostLoginFlow) {
      runOnWebWorker(() => {
        performConsentRedirectionFlow(routerState.currentRoute, null, true);
      });
    }
  }
};

export const applyRestrictions = (
  { user, company, operator, minimum }: Restrictions,
  shouldRedirect = true,
) => {
  const restrictionAction: RestrictionAction =
    company && user
      ? handleComplexRestrictions(user, company, operator, minimum)
      : handleRestrictions(user, operator, minimum);

  if (!restrictionAction.result) {
    if (shouldRedirect && 'redirectTo' in restrictionAction) {
      goTo(restrictionAction.redirectTo, undefined, {
        shouldNotPushToHistory: true,
      });
    }
    return false;
  }

  return true;
};

export const checkRestrictions = async (
  currentRoute: Module,
  previousRoute: Module,
) => {
  if (!currentRoute?.url) {
    return;
  }

  if (
    currentRoute?.restrictions &&
    !applyRestrictions(currentRoute.restrictions)
  ) {
    return;
  }

  const shouldGrantAccess = await (currentRoute.shouldGrantAccess
    ? currentRoute.shouldGrantAccess()
    : Promise.resolve(true));

  if (typeof shouldGrantAccess === 'object') {
    const params = shouldGrantAccess.params || false;
    if (!shouldGrantAccess.provideAccess) {
      goTo(shouldGrantAccess.redirectTo, params, {
        shouldNotPushToHistory: true,
      });
      return;
    }
  }

  if (!shouldGrantAccess) {
    return goTo('/404', undefined, { shouldNotPushToHistory: true });
  }

  const isAppointmentConfirmationPage =
    currentRoute.name === AppointmentsRoutes.APPOINTMENT_CONFIRMATION;

  if (isAppointmentConfirmationPage) {
    const willRedirectToConsents = await performConsentRedirectionFlow(
      currentRoute,
      previousRoute,
    );

    if (willRedirectToConsents) {
      return;
    }
  } else {
    // Additional safeguard: Check if we should defer the post-login flow before running consent check in web worker
    // This prevents a race condition where consents could show before signup form is completed
    // during IDP-initiated SSO flows
    const state = getStore().getState();
    const shouldDeferPostLoginFlow = state.consent.shouldResumePostLoginFlow;

    if (shouldDeferPostLoginFlow) {
      // Log when we skip the consent check due to deferred post-login flow
      AppLogger.logWithOptions({
        message:
          'Skipping consent check in web worker - post-login flow deferred',
        level: LogTypes.Log,
        feature: 'consent-flow',
        shouldLogToDatadog: true,
        context: {
          currentRoute: currentRoute.name,
          previousRoute: previousRoute?.name,
          shouldDeferPostLoginFlow,
        },
      });
    } else {
      runOnWebWorker(() => {
        performConsentRedirectionFlow(currentRoute, previousRoute);
      });
    }
  }
};

// location.search does not pick up the router params
// so we pass the search params to this function
export const checkZendeskRedirect = searchParams => {
  const returnTo = searchParams?.['return_to'];
  const hasUnaddressedZendeskRedirect =
    returnTo && location.pathname !== '/redirecting';

  if (hasUnaddressedZendeskRedirect) {
    window.open(
      `${location.protocol}//${location.host}/redirecting?message=employee_assistance.external_links.zendesk_redirecting_message&return_to=${returnTo}`,
      '_self',
    );
  }
};
